import {
  createContext,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react'
import { useKey } from 'react-use'
import gsap from 'gsap'

import Modal from '@/components/base/Modal'
import { useStore } from '@/contexts/store'

import ArticleModal from './components/Article'
import NewsletterModal from './components/Newsletter'

export type Modal = {
  slug?: string
  open?: boolean
  reveal?: boolean
}

export type ModalState = {
  newsletter?: Modal
  article?: Modal
}

export const ModalContext = createContext<
  | {
      // eslint-disable-next-line no-unused-vars
      setPreventModal: (prevent: boolean) => void
      // eslint-disable-next-line no-unused-vars
      openArticleModal: (slug: string) => void
    }
  | undefined
>(undefined)

const ModalProvider = ({ children }: PropsWithChildren) => {
  const { strings } = useStore()
  const [modal, setModal] = useState<ModalState>({})
  const [preventModal, setPreventModal] = useState(false)
  const behaviour = useMemo(() => strings?.behaviour, [strings])

  /**
   * Newsletter Modal
   */
  const openNewsletterModal = useCallback(
    () =>
      setModal((modal) => ({
        ...modal,
        newsletter: { ...modal.newsletter, reveal: false, open: true }
      })),
    []
  )
  const handleNewsletterModalClose = useCallback(
    () =>
      setModal((modal) => ({
        ...modal,
        newsletter: { ...modal.newsletter, open: false }
      })),
    []
  )
  const handleNewsletterModalClosed = useCallback(
    () =>
      setModal((modal) => ({
        ...modal,
        newsletter: { ...modal.newsletter, reveal: false }
      })),
    []
  )

  /**
   * Article Modal
   */
  const openArticleModal = useCallback(
    (slug?: string) =>
      setModal((modal) => ({
        ...modal,
        article: { ...modal.article, slug, reveal: false, open: true }
      })),
    []
  )
  const handleArticleModalClose = useCallback(
    () =>
      setModal((modal) => ({
        ...modal,
        article: { ...modal.article, open: false }
      })),
    []
  )
  const handleArticleModalClosed = useCallback(
    () =>
      setModal((modal) => ({
        ...modal,
        article: { ...modal.article, slug: undefined, reveal: false }
      })),
    []
  )

  useEffect(() => {
    if (!behaviour.is_active) return
    const delay = parseInt(behaviour.delay)
    gsap.delayedCall(delay, openNewsletterModal)
  }, [behaviour, openNewsletterModal])

  useKey(
    'n',
    () => {
      if (preventModal) return
      openNewsletterModal()
    },
    undefined,
    [preventModal]
  )

  useKey(
    'a',
    () => {
      if (preventModal || !modal.article?.slug) return
      openArticleModal()
    },
    undefined,
    [preventModal]
  )

  const contextValue = useMemo(
    () => ({ openArticleModal, setPreventModal }),
    [openArticleModal, setPreventModal]
  )

  return (
    <ModalContext.Provider value={contextValue}>
      {children}

      <Modal
        asChild={true}
        open={modal.article?.open}
        reveal={modal.article?.reveal}
        onClose={handleArticleModalClose}
        onClosed={handleArticleModalClosed}
      >
        <ArticleModal slug={modal.article?.slug} />
      </Modal>

      <Modal
        asChild={true}
        open={modal.newsletter?.open}
        reveal={modal.newsletter?.reveal}
        onClose={handleNewsletterModalClose}
        onClosed={handleNewsletterModalClosed}
      >
        <NewsletterModal />
      </Modal>
    </ModalContext.Provider>
  )
}

export default ModalProvider
